// PolymarketDetailModal — Perplexity-Style Multi-Outcome-Chart + Orderbook.
//
// Wird über die Polymarket-Page geöffnet wenn der User auf eine Markt-Card
// klickt. Zwei Sections:
//
// 1. Historischer Chart (Recharts LineChart) — pro Outcome eine Linie aus
//    /prices-history. YES/NO Default-Farben emerald/rose, weitere
//    Outcomes amber/sky/violet.
//
// 2. Orderbook-Snapshot (Bid/Ask-Wall-Visualisierung, top-5 Levels) —
//    /orderbook pro Outcome-Token. YES-Token-Default rendert.

import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ExternalLink, X, Zap } from 'lucide-react';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import {
  buildPolymarketPrompt,
  fetchOrderBook,
  fetchPricesHistory,
  type OrderBookResponse,
  type PricesHistoryPoint,
} from '@/lib/polymarket-api';

const OUTCOME_TONES = ['#10b981', '#f43f5e', '#f59e0b', '#0ea5e9', '#a78bfa'];

interface OutcomeSpec {
  /** Display-Name z.B. „YES". */
  label: string;
  /** condition_id (für prices-history). */
  conditionId: string;
  /** CLOB token_id (für orderbook). */
  tokenId?: string | null;
}

interface PolymarketDetailModalProps {
  question: string;
  outcomes: ReadonlyArray<OutcomeSpec>;
  slug?: string | null;
  onClose: () => void;
}

type IntervalKey = '6h' | '1d' | '1w' | '1m' | 'max';

const INTERVAL_OPTIONS: ReadonlyArray<{ key: IntervalKey; label: string }> = [
  { key: '6h', label: '6h' },
  { key: '1d', label: '24h' },
  { key: '1w', label: '7d' },
  { key: '1m', label: '30d' },
  { key: 'max', label: 'Max' },
];

interface ChartPoint {
  ts: number;
  [key: string]: number;
}

function mergeHistories(
  histories: ReadonlyArray<{ label: string; points: ReadonlyArray<PricesHistoryPoint> }>,
): ChartPoint[] {
  // Outer-join nach timestamp damit Recharts pro x-Tick alle Linien sieht.
  const byTs = new Map<number, ChartPoint>();
  for (const { label, points } of histories) {
    for (const p of points) {
      const existing = byTs.get(p.t);
      if (existing) existing[label] = p.p * 100;
      else byTs.set(p.t, { ts: p.t, [label]: p.p * 100 });
    }
  }
  return [...byTs.values()].sort((a, b) => a.ts - b.ts);
}

function fmtTick(ts: number): string {
  const d = new Date(ts * 1000);
  return `${d.getDate()}.${d.getMonth() + 1}`;
}

export function PolymarketDetailModal({
  question,
  outcomes,
  slug,
  onClose,
}: PolymarketDetailModalProps) {
  const accessToken = useAuthStore(selectAccessToken);
  const askEve = useEveDrawerStore((s) => s.askEve);
  const [interval, setInterval] = useState<IntervalKey>('1w');
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState<string | null>(null);
  const [orderbookOutcomeIdx, setOrderbookOutcomeIdx] = useState(0);
  const [orderbook, setOrderbook] = useState<OrderBookResponse | null>(null);
  const [orderbookLoading, setOrderbookLoading] = useState(false);

  useEffect(() => {
    if (!accessToken || outcomes.length === 0) return;
    let cancelled = false;
    setChartLoading(true);
    setChartError(null);
    Promise.all(
      outcomes.map((o) =>
        fetchPricesHistory(o.conditionId, { interval, accessToken })
          .then((res) => ({ label: o.label, points: res.history ?? [] }))
          .catch((e) => {
            console.warn('[polymarket-detail] prices-history failed:', o.label, e);
            return { label: o.label, points: [] as PricesHistoryPoint[] };
          }),
      ),
    )
      .then((histories) => {
        if (cancelled) return;
        setChartData(mergeHistories(histories));
      })
      .catch((e) => {
        if (cancelled) return;
        setChartError(e instanceof Error ? e.message : 'Chart-Laden fehlgeschlagen');
      })
      .finally(() => {
        if (!cancelled) setChartLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, outcomes, interval]);

  // Orderbook: nur fetchen wenn der Outcome eine tokenId hat.
  useEffect(() => {
    const o = outcomes[orderbookOutcomeIdx];
    if (!o || !o.tokenId || !accessToken) {
      setOrderbook(null);
      return;
    }
    let cancelled = false;
    setOrderbookLoading(true);
    fetchOrderBook(o.tokenId, accessToken)
      .then((data) => {
        if (cancelled) return;
        setOrderbook(data);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn('[polymarket-detail] orderbook failed:', e);
        setOrderbook(null);
      })
      .finally(() => {
        if (!cancelled) setOrderbookLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [outcomes, orderbookOutcomeIdx, accessToken]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="glass-strong w-full max-w-3xl rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">Polymarket</div>
            <h3 className="mt-1 text-base font-bold leading-tight">{question}</h3>
            <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
              {outcomes.map((o, i) => (
                <span
                  key={o.label}
                  className="rounded-full px-2 py-0.5 font-bold"
                  style={{ color: OUTCOME_TONES[i % OUTCOME_TONES.length] }}
                >
                  ● {o.label}
                </span>
              ))}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {slug ? (
              <a
                href={`https://polymarket.com/event/${slug}`}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Auf Polymarket öffnen"
                className="text-neutral-500 hover:text-amber-400"
              >
                <ExternalLink className="size-4" />
              </a>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="text-neutral-500 hover:text-neutral-200"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* Chart-Sektion */}
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">
              Wahrscheinlichkeit-Verlauf
            </div>
            <div className="flex items-center gap-1">
              {INTERVAL_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setInterval(opt.key)}
                  className={
                    interval === opt.key
                      ? 'rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300'
                      : 'rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-neutral-400 hover:bg-white/5'
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-56 rounded-xl border border-white/5 bg-neutral-950/40 p-2">
            {chartError ? (
              <div className="grid h-full place-items-center text-[12px] text-rose-300">
                {chartError}
              </div>
            ) : chartLoading ? (
              <div className="grid h-full place-items-center text-[12px] text-neutral-500">
                Lade Chart…
              </div>
            ) : chartData.length === 0 ? (
              <div className="grid h-full place-items-center text-[12px] text-neutral-500">
                Keine Daten für dieses Intervall.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="ts"
                    stroke="#52525b"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={fmtTick}
                  />
                  <YAxis
                    domain={[0, 100]}
                    stroke="#52525b"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(20,20,24,0.95)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8,
                      fontSize: 11,
                    }}
                    labelFormatter={(v) => {
                      const d = new Date((v as number) * 1000);
                      return d.toLocaleString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                    }}
                    formatter={(value: number, name) => [`${value.toFixed(1)} %`, name]}
                  />
                  {outcomes.map((o, i) => (
                    <Line
                      key={o.label}
                      type="monotone"
                      dataKey={o.label}
                      stroke={OUTCOME_TONES[i % OUTCOME_TONES.length]}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Orderbook-Sektion */}
        {outcomes.some((o) => o.tokenId) ? (
          <div className="mb-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                Orderbook · Top-5 Levels
              </div>
              <div className="flex items-center gap-1">
                {outcomes.map((o, i) => (
                  <button
                    key={o.label}
                    type="button"
                    onClick={() => setOrderbookOutcomeIdx(i)}
                    disabled={!o.tokenId}
                    className={
                      orderbookOutcomeIdx === i && o.tokenId
                        ? 'rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300'
                        : 'rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-neutral-400 hover:bg-white/5 disabled:opacity-40'
                    }
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            {orderbookLoading ? (
              <div className="rounded-xl border border-white/5 bg-neutral-950/40 p-6 text-center text-[12px] text-neutral-500">
                Lade Orderbook…
              </div>
            ) : orderbook ? (
              <OrderBookSection orderbook={orderbook} />
            ) : (
              <div className="rounded-xl border border-white/5 bg-neutral-950/40 p-6 text-center text-[12px] text-neutral-500">
                Kein Orderbook verfügbar für dieses Outcome.
              </div>
            )}
          </div>
        ) : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              // Aus chartData (merged history je outcome) pro Linie die Punkte
              // wieder extrahieren.
              const histories = outcomes.map((o) => ({
                label: o.label,
                points: chartData
                  .filter((p) => typeof p[o.label] === 'number')
                  .map((p) => ({ ts: p.ts, pct: p[o.label] as number })),
              }));
              const activeOutcome = outcomes[orderbookOutcomeIdx];
              const orderbookCompact = orderbook && activeOutcome
                ? {
                    label: activeOutcome.label,
                    bids: (orderbook.bids ?? [])
                      .map((b) => ({ price: Number(b.price), size: Number(b.size) }))
                      .filter((b) => Number.isFinite(b.price) && Number.isFinite(b.size))
                      .sort((a, b) => b.price - a.price),
                    asks: (orderbook.asks ?? [])
                      .map((a) => ({ price: Number(a.price), size: Number(a.size) }))
                      .filter((a) => Number.isFinite(a.price) && Number.isFinite(a.size))
                      .sort((a, b) => a.price - b.price),
                  }
                : null;
              const lastByLabel = (label: string): number | null => {
                const series = histories.find((h) => h.label === label)?.points ?? [];
                const last = series[series.length - 1];
                return last ? last.pct : null;
              };
              const prompt = buildPolymarketPrompt({
                question,
                outcomes: outcomes.map((o) => ({ label: o.label, pct: lastByLabel(o.label) })),
                history: histories.filter((h) => h.points.length > 0),
                orderbook: orderbookCompact,
              });
              askEve(prompt, { displayText: question });
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-[12px] font-bold text-neutral-950 hover:bg-amber-400"
          >
            <Zap className="size-3.5" />
            Frag EVE zu diesem Markt
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderBookSection({ orderbook }: { orderbook: OrderBookResponse }) {
  const bids = [...(orderbook.bids ?? [])]
    .map((b) => ({ price: Number(b.price), size: Number(b.size) }))
    .filter((b) => Number.isFinite(b.price) && Number.isFinite(b.size))
    .sort((a, b) => b.price - a.price)
    .slice(0, 5);
  const asks = [...(orderbook.asks ?? [])]
    .map((a) => ({ price: Number(a.price), size: Number(a.size) }))
    .filter((a) => Number.isFinite(a.price) && Number.isFinite(a.size))
    .sort((a, b) => a.price - b.price)
    .slice(0, 5);
  const maxSize = Math.max(
    ...bids.map((b) => b.size),
    ...asks.map((a) => a.size),
    1,
  );
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] p-3">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
          Bids ({bids.length})
        </div>
        <ul className="space-y-0.5">
          {bids.map((b, i) => (
            <li key={i} className="relative grid grid-cols-2 text-[11px]">
              <span
                className="absolute inset-y-0 left-0 -z-0 rounded bg-emerald-500/15"
                style={{ width: `${(b.size / maxSize) * 100}%` }}
              />
              <span className="z-10 font-mono text-emerald-300">{(b.price * 100).toFixed(2)}¢</span>
              <span className="z-10 text-right font-mono text-neutral-400">{b.size.toFixed(0)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-rose-500/15 bg-rose-500/[0.03] p-3">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-rose-400">
          Asks ({asks.length})
        </div>
        <ul className="space-y-0.5">
          {asks.map((a, i) => (
            <li key={i} className="relative grid grid-cols-2 text-[11px]">
              <span
                className="absolute inset-y-0 right-0 -z-0 rounded bg-rose-500/15"
                style={{ width: `${(a.size / maxSize) * 100}%` }}
              />
              <span className="z-10 font-mono text-rose-300">{(a.price * 100).toFixed(2)}¢</span>
              <span className="z-10 text-right font-mono text-neutral-400">{a.size.toFixed(0)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
